import React, { useState, useEffect } from "react";
import { Modal, Button, Space, Typography } from "antd";
import {
	GiftOutlined,
	ClockCircleOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
} from "@ant-design/icons";
import planoContratualService from "services/PlanoContratualService";
import sorteioRespostaService from "services/SorteioRespostaService";

const { Title, Paragraph, Text } = Typography;

const STORAGE_KEY = "sorteio_mensalidade_modal_2026_05_29";
const FORM_URL =
	"https://docs.google.com/forms/d/e/1FAIpQLSfmD7vgbkj7gOI_mC1_5vYq47jQUcpI-34zYG6tT1rFogma6Q/viewform?usp=sharing&ouid=113832653238751691165";
// Não exibir após o fim do dia 29/05/2026 (a partir de 30/05 00:00, horário local).
const DEADLINE = new Date(2026, 4, 30);

const isSameLocalDay = (a, b) =>
	a.getFullYear() === b.getFullYear() &&
	a.getMonth() === b.getMonth() &&
	a.getDate() === b.getDate();

const SorteioMensalidadeModal = () => {
	const [visible, setVisible] = useState(false);
	const [isReprompt, setIsReprompt] = useState(false);

	useEffect(() => {
		sincronizarRespostaPendente();
		checkPlanoAndShowModal();
	}, []);

	const checkPlanoAndShowModal = async () => {
		try {
			if (new Date() >= DEADLINE) return;

			let reprompt = false;
			const storedData = localStorage.getItem(STORAGE_KEY);
			if (storedData) {
				const data = JSON.parse(storedData);

				// Já deu uma resposta limpa depois do ajuste de desambiguação
				// ("já respondi" ou "não quero"): respeita a escolha pra sempre.
				if (data.reason) return;

				// Sem reason: ou clicou "lembrar mais tarde", ou é um registro
				// legado de "já respondi"/"não quero" (que gravavam igual e são
				// indistinguíveis). Nos dois casos re-exibimos no máximo 1x por
				// dia pra tentar capturar uma resposta limpa.
				if (
					data.lastShown &&
					isSameLocalDay(new Date(data.lastShown), new Date())
				) {
					return;
				}

				// Já viu o modal antes: mostramos o reforço de "já respondi".
				reprompt = true;
			}

			const planoAtual = await planoContratualService.getPlanoAtual();
			const valorFixo = parseFloat(
				planoAtual?.plano_contratual?.valor_fixo || 0,
			);
			if (valorFixo > 0) {
				setIsReprompt(reprompt);
				setVisible(true);
			}
		} catch (error) {
			console.error("Erro ao verificar plano para modal do sorteio:", error);
		}
	};

	const persistAndClose = (payload) => {
		localStorage.setItem(
			STORAGE_KEY,
			JSON.stringify({ ...payload, lastShown: new Date().toISOString() }),
		);
		setVisible(false);
	};

	// Envia ao backend que este profissional confirmou "já respondi", pra
	// montar a lista do sorteio (o formulário antigo não capturava o e-mail).
	// Silencioso: se falhar, tenta de novo no próximo load via
	// sincronizarRespostaPendente().
	const enviarRespostaSorteio = (origem) => {
		let payload = null;
		try {
			payload = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
		} catch (e) {
			payload = null;
		}

		sorteioRespostaService
			.registrarResposta({ origem, payload })
			.then(() => {
				try {
					const data = JSON.parse(
						localStorage.getItem(STORAGE_KEY) || "{}",
					);
					localStorage.setItem(
						STORAGE_KEY,
						JSON.stringify({ ...data, syncedToServer: true }),
					);
				} catch (e) {
					/* ignore */
				}
			})
			.catch(() => {
				/* silencioso; retry no próximo load */
			});
	};

	// Rede de segurança: se o profissional clicou "já respondi" mas o envio
	// falhou, reenvia no próximo carregamento da plataforma.
	const sincronizarRespostaPendente = () => {
		try {
			const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
			if (!data) return;
			if (data.reason === "ja_respondi" && !data.syncedToServer) {
				enviarRespostaSorteio("ja_respondi");
			}
		} catch (e) {
			/* ignore */
		}
	};

	const handleRemindLater = () => persistAndClose({ remindLater: true });

	const handleAlreadyAnswered = () => {
		persistAndClose({ dontShowAgain: true, reason: "ja_respondi" });
		enviarRespostaSorteio("ja_respondi");
	};

	const handleNotInterested = () =>
		persistAndClose({ dontShowAgain: true, reason: "nao_quero" });

	const handleOpenForm = () => {
		window.open(FORM_URL, "_blank", "noopener,noreferrer");
	};

	return (
		<Modal
			open={visible}
			onCancel={handleRemindLater}
			footer={null}
			width={460}
			centered
			closable={false}
			bodyStyle={{ padding: 0, borderRadius: 12, overflow: "hidden" }}
			style={{ borderRadius: 12 }}
		>
			<div
				style={{
					background:
						"linear-gradient(135deg, #ff8034 0%, #ff5722 100%)",
					padding: "28px 24px",
					textAlign: "center",
					color: "#fff",
				}}
			>
				<GiftOutlined style={{ fontSize: 48, marginBottom: 12 }} />
				<Title
					level={3}
					style={{
						color: "#fff",
						margin: 0,
						fontSize: 22,
						fontWeight: 700,
					}}
				>
					Sorteio: 1 ano de mensalidade
				</Title>
				<Text style={{ color: "rgba(255,255,255,0.92)", fontSize: 14 }}>
					Participe respondendo a uma pesquisa rápida
				</Text>
			</div>

			<div style={{ padding: "24px 28px 20px" }}>
				{isReprompt && (
					<Paragraph
						style={{
							fontSize: 13,
							marginBottom: 16,
							background: "#fff7e6",
							border: "1px solid #ffd591",
							borderRadius: 6,
							padding: "10px 12px",
							color: "#ad6800",
						}}
					>
						Se você já respondeu nossa pesquisa, é só confirmar em{" "}
						<strong>"Já respondi"</strong> para entrar no sorteio.
					</Paragraph>
				)}
				<Paragraph style={{ fontSize: 14, marginBottom: 12 }}>
					Estamos sorteando <strong>1 ano de mensalidade</strong> entre
					os profissionais que responderem a nossa pesquisa. O sorteio
					cobre apenas o <strong>valor fixo</strong> do plano; o valor
					variável de R$ 1,99 por aluno continua sendo cobrado
					normalmente.
				</Paragraph>
				<Paragraph
					style={{
						fontSize: 14,
						marginBottom: 12,
						textAlign: "center",
						fontWeight: 600,
						color: "#ff5722",
					}}
				>
					Você pode responder até <u>29/05</u>. O sorteio será
					realizado em <u>30/05</u>.
				</Paragraph>
				<Paragraph
					style={{
						fontSize: 13,
						marginBottom: 20,
						background: "#f6ffed",
						border: "1px solid #b7eb8f",
						borderRadius: 6,
						padding: "10px 12px",
						color: "#237804",
					}}
				>
					Sorteio <strong>exclusivo para assinantes</strong> da
					plataforma — sua chance de ganhar é muito maior!
				</Paragraph>

				<Button
					type="primary"
					size="large"
					block
					onClick={handleOpenForm}
					style={{
						height: 46,
						fontWeight: 600,
						marginBottom: 16,
					}}
				>
					Responder pesquisa
				</Button>

				<Space
					direction="vertical"
					size={6}
					style={{ width: "100%" }}
				>
					<Button
						icon={<ClockCircleOutlined />}
						onClick={handleRemindLater}
						block
					>
						Lembrar mais tarde
					</Button>
					<Button
						icon={<CheckCircleOutlined />}
						onClick={handleAlreadyAnswered}
						block
					>
						Já respondi
					</Button>
					<Button
						type="text"
						icon={<CloseCircleOutlined />}
						onClick={handleNotInterested}
						block
						style={{ color: "rgba(0,0,0,0.45)" }}
					>
						Não quero participar
					</Button>
				</Space>
			</div>
		</Modal>
	);
};

export default SorteioMensalidadeModal;
